import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { PageHeader } from "../components/shell/PageHeader";
import { Button } from "../components/ui/Button";
import { EmptyState, ErrorNotice, Notice, SkeletonRows } from "../components/ui/Feedback";
import { Field, SelectField } from "../components/ui/Input";
import { Pill } from "../components/ui/Status";
import type { Column } from "../components/ui/Table";
import { Cell, Pagination, TableFrame, TableHead, TableRow } from "../components/ui/Table";
import { Metric, MicroLabel } from "../components/ui/Text";
import { useAlertSummary } from "../lib/alerts";
import { getPage, post } from "../lib/api";
import { count, since } from "../lib/format";
import { useSession } from "../lib/session";
import type { StatusTone } from "../lib/status";
import type { AlertSeverity, AlertSummary, SystemAlert } from "../lib/types";

/**
 * Alerts — what the server cannot fix by itself, and who owns each one.
 *
 * ## Why `Seen` is a column and not a detail
 *
 * Because it is the difference between the two situations this page exists to
 * tell apart. A refund whose rail blipped once and a refund that has been refusing
 * every thirty seconds since yesterday raise the *same* alert with the same
 * summary; the count and the first-seen time are the only things that distinguish
 * them, and a panel that hid either would flatten an outage into a curiosity.
 *
 * ## Acknowledging is not resolving, and the table says so
 *
 * `Owner` fills in when somebody takes an alert, and the row stays. Operators work
 * from a list of open conditions, and one where "I have seen this" removed the row
 * would lose every problem the moment it was noticed. Only `Resolve` — which asks
 * for a note — closes it.
 *
 * ## Details are shown, not linked
 *
 * The raiser's context object is printed under the summary of an expanded row,
 * because it is the actual working material: the order id, the provider
 * reference, the attempt count. Making it a second navigation would mean an
 * operator diagnoses from memory.
 */

const COLUMNS: readonly Column[] = [
  { key: "seen", label: "Last seen", width: "10%" },
  { key: "severity", label: "Severity", width: "10%" },
  { key: "condition", label: "Condition" },
  { key: "subject", label: "Subject", width: "16%" },
  { key: "count", label: "Seen", numeric: true, width: "8%" },
  { key: "owner", label: "Owner", width: "12%" },
  { key: "act", label: "", width: "16%" },
];

/**
 * The panel's existing vocabulary, reused rather than extended. `faulted` is the
 * only tone in this design system that reads as "wrong", and inventing a fourth
 * alert colour would put two scales for badness on one screen.
 */
const SEVERITY_TONE: Readonly<Record<AlertSeverity, StatusTone>> = {
  critical: "faulted",
  error: "faulted",
  warning: "transitional",
};

export function Alerts() {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { can } = useSession();
  const [params, setParams] = useSearchParams();
  const [expanded, setExpanded] = useState<string | undefined>(undefined);
  const [notice, setNotice] = useState<string | undefined>(undefined);

  const status = params.get("status") ?? "";
  const severity = params.get("severity") ?? "";
  const page = positiveInt(params.get("page")) ?? 1;

  const alerts = useQuery({
    queryKey: ["alerts", { status, severity, page }],
    queryFn: () => getPage<SystemAlert>("/alerts", { query: { status, severity, page } }),
    placeholderData: (previous) => previous,
  });

  /**
   * The masthead's figures come from the summary endpoint, not from the rows: the
   * whole point of "two critical alerts are open" is that it does not change when
   * somebody filters the table to look at warnings. Shared with the rail's badge —
   * see `lib/alerts.ts`.
   */
  const summary = useAlertSummary();

  const rows = alerts.data?.rows ?? [];
  const canAcknowledge = can("alerts.acknowledge");
  const canResolve = can("alerts.resolve");
  const canAct = canAcknowledge || canResolve;

  function refresh() {
    void queryClient.invalidateQueries({ queryKey: ["alerts"] });
  }

  const acknowledge = useMutation({
    mutationFn: (id: string) => post<SystemAlert>(`/alerts/${id}/acknowledge`, {}),
    onSuccess: (alert) => {
      setNotice(`${alert.code} acknowledged.`);
      refresh();
    },
  });

  function setFilter(key: string, value: string) {
    const next = new URLSearchParams(params);
    if (value === "") next.delete(key);
    else next.set(key, value);
    next.delete("page");
    setParams(next, { replace: true });
  }

  return (
    <>
      <PageHeader
        eyebrow="Operations"
        title="Alerts"
        metrics={
          <>
            <Metric label="Matching" value={count(alerts.data?.meta?.total)} hint="these filters" />
            <Metric
              label="Open critical"
              value={count(openCritical(summary.data))}
              hint="money or safety"
              tone={(openCritical(summary.data) ?? 0) > 0 ? "signal" : "muted"}
            />
          </>
        }
        lede="Conditions this server could not resolve on its own. Each row is a condition that is still true — the count says how many times it has been seen since it started, and acknowledging one records who owns it without closing it."
      />

      <form
        onSubmit={(event) => event.preventDefault()}
        className="grid grid-cols-1 gap-x-6 gap-y-4 md:grid-cols-2 lg:grid-cols-4"
      >
        <SelectField
          label="Status"
          value={status}
          onChange={(event) => setFilter("status", event.target.value)}
        >
          {/* Open is the server's default too, so the empty value is not "any". */}
          <option value="">Open</option>
          <option value="resolved">Resolved</option>
          <option value="all">All</option>
        </SelectField>

        <SelectField
          label="Severity"
          value={severity}
          onChange={(event) => setFilter("severity", event.target.value)}
        >
          <option value="">Any</option>
          <option value="critical">Critical</option>
          <option value="error">Error</option>
          <option value="warning">Warning</option>
        </SelectField>
      </form>

      {notice === undefined ? null : <Notice tone="ok">{notice}</Notice>}
      {alerts.isError ? (
        <ErrorNotice error={alerts.error} onRetry={() => void alerts.refetch()} />
      ) : null}
      {acknowledge.isError ? <ErrorNotice error={acknowledge.error} /> : null}

      <div className="flex flex-col gap-3">
        <TableFrame>
          <TableHead columns={COLUMNS} />
          {alerts.isPending ? (
            <SkeletonRows rows={8} columns={COLUMNS.length} />
          ) : (
            <tbody>
              {rows.map((row, index) => (
                <TableRow
                  key={row.id}
                  index={index}
                  onActivate={() => setExpanded(expanded === row.id ? undefined : row.id)}
                >
                  <Cell muted>{since(row.lastSeenAt)}</Cell>
                  <Cell>
                    <Pill tone={SEVERITY_TONE[row.severity]}>{row.severity}</Pill>
                  </Cell>
                  <Cell>
                    <div className="flex flex-col gap-0.5">
                      <span className="font-medium text-ink">{row.summary}</span>
                      <span className="text-micro tracking-normal normal-case text-ink-faint">
                        {row.code}
                        {row.resolvedAt === null ? "" : " · resolved"}
                      </span>
                      {expanded === row.id ? <Details alert={row} /> : null}
                    </div>
                  </Cell>
                  <Cell>
                    {row.subjectType === "order" && row.subjectId !== null ? (
                      <button
                        type="button"
                        className="text-data text-signal underline-offset-2 hover:underline"
                        onClick={(event) => {
                          // The row's own activation toggles details; this is a
                          // different destination, so it must not also fire.
                          event.stopPropagation();
                          navigate(`/public-orders?order=${encodeURIComponent(row.subjectId ?? "")}`);
                        }}
                      >
                        Open order
                      </button>
                    ) : (
                      <span className="text-data text-ink-muted">{row.subjectType ?? "—"}</span>
                    )}
                  </Cell>
                  <Cell numeric>{count(row.occurrences)}</Cell>
                  <Cell muted>{row.acknowledgedAt === null ? "—" : "Acknowledged"}</Cell>
                  <Cell>
                    {row.resolvedAt !== null || !canAct ? null : (
                      <div className="flex items-baseline gap-3">
                        {row.acknowledgedAt === null && canAcknowledge ? (
                          <Button
                            variant="ghost"
                            disabled={acknowledge.isPending}
                            onClick={(event) => {
                              event.stopPropagation();
                              acknowledge.mutate(row.id);
                            }}
                          >
                            Acknowledge
                          </Button>
                        ) : null}
                        {canResolve ? (
                          <ResolveControl
                            alert={row}
                            onResolved={(code) => {
                              setNotice(`${code} resolved.`);
                              refresh();
                            }}
                          />
                        ) : null}
                      </div>
                    )}
                  </Cell>
                </TableRow>
              ))}
            </tbody>
          )}
        </TableFrame>

        {!alerts.isPending && rows.length === 0 ? (
          <EmptyState
            title={
              status === "" && severity === ""
                ? "Nothing is wrong that this server could not fix itself."
                : "No alerts match these filters."
            }
            detail="Alerts are raised by the server, never by hand: a stuck refund, a settlement that could not be confirmed, an invariant that turned out not to hold. An empty list here is the intended state — and a resolved alert stays in the history rather than being deleted, so switching Status to All will show what has happened before."
          />
        ) : null}

        <Pagination
          meta={alerts.data?.meta}
          shown={rows.length}
          onPage={(next) => {
            const nextParams = new URLSearchParams(params);
            if (next <= 1) nextParams.delete("page");
            else nextParams.set("page", String(next));
            setParams(nextParams);
          }}
        />
      </div>
    </>
  );
}

/**
 * The raiser's context, as key–value pairs. Printed rather than pretty-formatted:
 * the keys are the server's own field names, and renaming them for display would
 * break the one workflow this supports — reading a figure here and grepping the
 * logs for it.
 */
function Details({ alert }: { readonly alert: SystemAlert }) {
  const entries = Object.entries(alert.details ?? {});

  return (
    <div className="mt-2 flex flex-col gap-1.5 border-l-2 border-rule pl-3">
      <MicroLabel>First seen {since(alert.firstSeenAt)}</MicroLabel>
      {entries.length === 0 ? (
        <span className="text-micro tracking-normal normal-case text-ink-faint">
          No further detail was recorded.
        </span>
      ) : (
        <dl className="grid grid-cols-[auto_1fr] gap-x-3 gap-y-0.5">
          {entries.map(([key, value]) => (
            <div key={key} className="col-span-2 grid grid-cols-subgrid">
              <dt className="text-micro tracking-normal normal-case text-ink-faint">{key}</dt>
              <dd className="text-data break-all text-ink">{render(value)}</dd>
            </div>
          ))}
        </dl>
      )}
      {alert.resolutionNote === null ? null : (
        <span className="text-data text-ink-muted">Resolved: {alert.resolutionNote}</span>
      )}
    </div>
  );
}

/**
 * Resolve, with the note inline rather than in a dialog.
 *
 * The note is the only place the *reason* a condition was closed survives, and a
 * modal is where optional fields go to be skipped. Two clicks either way, and this
 * one keeps the alert's own row on screen while it is being written.
 */
function ResolveControl({
  alert,
  onResolved,
}: {
  readonly alert: SystemAlert;
  readonly onResolved: (code: string) => void;
}) {
  const [open, setOpen] = useState(false);
  const [note, setNote] = useState("");

  const resolve = useMutation({
    mutationFn: () => post<SystemAlert>(`/alerts/${alert.id}/resolve`, note === "" ? {} : { note }),
    onSuccess: () => {
      setOpen(false);
      setNote("");
      onResolved(alert.code);
    },
  });

  if (!open) {
    return (
      <Button
        variant="ghost"
        onClick={(event) => {
          event.stopPropagation();
          setOpen(true);
        }}
      >
        Resolve
      </Button>
    );
  }

  return (
    <form
      className="flex flex-col gap-2"
      /**
       * Both handlers stop the row's own activation, and the keyboard one is not
       * merely there to satisfy a lint rule: the row toggles its details on Enter,
       * and Enter inside the note field must submit the resolution instead of
       * collapsing the row out from under the person typing it.
       */
      onClick={(event) => event.stopPropagation()}
      onKeyDown={(event) => event.stopPropagation()}
      onSubmit={(event) => {
        event.preventDefault();
        resolve.mutate();
      }}
    >
      <Field
        label="What was done"
        value={note}
        placeholder="refunded by hand in the portal"
        onChange={(event) => setNote(event.target.value)}
      />
      <div className="flex items-baseline gap-3">
        <Button type="submit" disabled={resolve.isPending}>
          Resolve
        </Button>
        <Button variant="ghost" onClick={() => setOpen(false)}>
          Cancel
        </Button>
      </div>
      {resolve.isError ? <ErrorNotice error={resolve.error} /> : null}
    </form>
  );
}

function render(value: unknown): string {
  if (value === null) return "null";
  if (typeof value === "string" || typeof value === "number" || typeof value === "boolean") {
    return String(value);
  }
  return JSON.stringify(value);
}

function openCritical(summary: AlertSummary | undefined): number | undefined {
  return summary?.open.critical;
}

function positiveInt(raw: string | null): number | undefined {
  if (raw === null || !/^\d+$/.test(raw)) return undefined;
  const value = Number(raw);
  return value >= 1 ? value : undefined;
}
